import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { useCallback, useEffect, useState } from "react";
import type { Market } from "../lib/types";
import { getToken2022Ata, readU64 } from "../lib/token2022";

interface Balances {
  solLamports: bigint;
  tokenRaw: bigint;
  loading: boolean;
}

export function useBalances(
  connection: Connection,
  owner: PublicKey | null,
  market?: Market,
) {
  const [balances, setBalances] = useState<Balances>({
    solLamports: 0n,
    tokenRaw: 0n,
    loading: false,
  });

  const refresh = useCallback(async () => {
    if (!owner || !market) {
      setBalances({ solLamports: 0n, tokenRaw: 0n, loading: false });
      return;
    }
    setBalances((current) => ({ ...current, loading: true }));
    const tokenAccount = getToken2022Ata(new PublicKey(market.mint), owner);
    const [solLamports, tokenInfo] = await Promise.all([
      connection.getBalance(owner, "confirmed"),
      connection.getAccountInfo(tokenAccount, "confirmed"),
    ]);
    setBalances({
      solLamports: BigInt(solLamports),
      tokenRaw: tokenInfo ? readU64(tokenInfo.data, 64) : 0n,
      loading: false,
    });
  }, [connection, market, owner]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { ...balances, refresh };
}
